import FillingSpinner from 'components/FillingSpinner'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePickerSingle from 'components/Form/DatePickerSingle'
import { DATE_PLACEHOLDER } from 'constant'
import { FormData, formDataDefault, FormDataErrors } from './types'
import Input from 'components/Form/Input'
import Button from 'components/Form/Button'
import CommonReport from '../../components/CommonReport'
import '../../Report.scss'
import { useSelector } from 'react-redux'
import { RootState, store } from '../../../../store'
import CommonReportsService from '../../services/common-reports.service'
import { reportActions } from '../../store/report-slice'
import { FETCH_REPORT_DELAY } from '../../utils/report.constants'

const MediaReport: React.FC = () => {
  const reportType = 'media'
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>(formDataDefault)
  const [errors, setErrors] = useState<FormDataErrors>()
  const { media } = useSelector((state: RootState) => state.report)

  // We need it to fetch reports on page load
  useEffect(() => {
    void CommonReportsService.fetchReports(reportType)
  }, [])

  const generateReport = async () => {
    const inputErrors = CommonReportsService.validateInputWithIds(formData.asIds)

    setErrors({
      ...errors,
      asIds: [...inputErrors],
    })

    if (inputErrors.length) {
      return
    }

    const splitIds: string[] = formData.asIds.split(',').map(id => id.trim())

    Promise.resolve(store.dispatch(reportActions.setLoading({ key: reportType, loading: true })))
      .then(() =>
        CommonReportsService.createMediaReport({
          asIds: splitIds,
          startDate: formData.campaignStartDate,
          endDate: formData.campaignEndDate,
        })
      )
      .then(() => new Promise(resolve => setTimeout(resolve, FETCH_REPORT_DELAY)))
      .then(() => CommonReportsService.fetchReports(reportType))
      .finally(
        () => void store.dispatch(reportActions.setLoading({ key: reportType, loading: false }))
      )
  }

  const Form = (): ReactNode => (
    <div className='Report__form'>
      <Input
        id='asIds'
        title={t('report.mediaIds')}
        placeholder='89716, 91365, 91835'
        help={t('report.mediaIdsHelp')}
        value={formData.asIds.toString()}
        onChange={(value: string) =>
          void setFormData({
            ...formData,
            asIds: value,
          })
        }
        errors={errors?.asIds}
        disabled={media.loading}
      />

      <DatePickerSingle
        id='campaignStartDate'
        value={formData.campaignStartDate ?? ''}
        title={t('common.from')}
        placeholder={DATE_PLACEHOLDER}
        onChange={(value: string) => {
          setFormData({ ...formData, campaignStartDate: !value ? null : value })
        }}
        isOutsideRange={() => false}
        disabled={media.loading}
      />

      <DatePickerSingle
        id='campaignEndDate'
        value={formData.campaignEndDate ?? ''}
        title={t('common.to')}
        placeholder={DATE_PLACEHOLDER}
        onChange={(value: string) => {
          setFormData({ ...formData, campaignEndDate: !value ? null : value })
        }}
        isOutsideRange={() => false}
        disabled={media.loading}
      />

      <div className='Report__generate-button-container'>
        <Button
          onClick={generateReport}
          disabled={media.loading}
        >
          {t('report.generate')}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {Form()}
      {media.loading ? (
        <FillingSpinner className='Report__loading' />
      ) : (
        CommonReport.ReportTable(media.reports, t)
      )}
    </>
  )
}

export default MediaReport
